<template>
  <div id="athletics">
    <Breadcrumbs
      :crumbs="[
        { name: globals.info.school_name, link: `${pathPrefix}/` },
        { name: sectionName, link: `${pathPrefix}/athletics` },
      ]"
    />
    <div class="page-container">
      <Filters
        :filters="filters"
        name="athletics"
        placeholder="Sports"
        @on-filter="handleFilterChange"
      />
      <LoadingIndicator v-if="isLoading" />
      <div
        v-for="(game, idx) in games"
        v-else
        :id="game.id"
        :key="game.id"
        class="page-container-item"
      >
        <div class="col-left">
          <div class="datetime">
            <div class="date">{{ game.month }} {{ game.day }}</div>
            <div class="time">
              {{ game.time }}
            </div>
          </div>
        </div>
        <div class="col-right">
          <span class="sport-tag">{{ getTagName(game.section_filter_id) }}</span>
          <div
            class="scores-box closed"
            :class="[game.multi_line ? tournament : '', openScores.includes(idx) ? 'open' : '']"
            @click="handleShowScores(game, idx)"
          >
            <template v-if="game.title">
              <div class="title">
                {{ game.title }}
              </div>
            </template>
            <template v-else-if="game.home_score && game.away_score">
              <div class="teams">
                <div class="team">
                  {{ game.away_team }}<span>&nbsp;{{ $t('pages.shared.at') }}&nbsp;</span>
                </div>
                <div class="team home-team">
                  {{ game.home_team }}
                </div>
              </div>
              <div
                v-if="!game.multi_line"
                class="scores"
              >
                <div class="score">
                  {{ game.away_score }}
                </div>
                <div class="score home-score">
                  {{ game.home_score }}
                </div>
              </div>
            </template>
            <template v-else>
              <div>
                {{ $t('pages.athletics.gameLocation', { awayTeam: game.away_team, homeTeam: game.home_team }) }}
              </div>
            </template>
          </div>
          <div
            v-if="game.address"
            class="location"
            @click="handleShowScores(game, idx)"
          >
            <span id="map-info"> {{ game.address }}</span>
            <a
              id="map-link"
              aria-labelledby="map-link map-info"
              :href="getDirections(game.address)"
              target="_blank"
              >{{ $t('pages.athletics.map') }}</a
            >
          </div>
          <div
            v-if="openScores.includes(idx)"
            class="tournament-scores"
          >
            <span
              v-for="{ id, text } in game.score_lines"
              :key="id"
            >
              {{ text }}<br />
            </span>
          </div>
        </div>
      </div>

      <pagination
        v-if="totalPages > 1"
        :current-page="currentPage"
        :total-pages="totalPages"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { onBeforeRouteUpdate, useRoute } from 'vue-router';
import { useMainStore } from '../../stores/mainStore';
import isEqual from 'lodash/isEqual';

import Breadcrumbs from '../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '../../components/global/LoadingIndicator.vue';
import Filters from '../../components/global/FiltersDropdown.vue';
import Pagination from '../../components/global/Pagination.vue';
import { setupAndFetchAthletics } from '../../helpers/section/sections.helper';

defineOptions({ name: 'Athletics' });

const mainStore = useMainStore();
const route = useRoute();
const { $axios } = useNuxtApp();

const isLoading = ref(false);
const games = ref([]);
const filters = ref([]);
const currentPage = ref(0);
const totalPages = ref(0);
const tournament = 'tournament';
const openScores = ref([]);

const { data: initialData } = await useAsyncData('athletics', () =>
  setupAndFetchAthletics(mainStore, route.query, $axios),
);

if (initialData.value) {
  games.value = initialData.value.games ?? [];
  filters.value = initialData.value.filters ?? [];
  totalPages.value = initialData.value.totalPages ?? 0;
  currentPage.value = initialData.value.currentPage ?? 0;
}
const globals = computed(() => mainStore.globals);
const pathPrefix = computed(() => mainStore.pathPrefix);
const sectionName = computed(() => mainStore.sectionName);

useHead({
  title: sectionName.value,
});

watch(
  () => route.query.page_no,
  (newVal, oldVal) => {
    if (import.meta.client && oldVal !== newVal) {
      window.scrollTo(0, 0);
    }
  },
);

onBeforeRouteUpdate((to, from, next) => {
  if (
    import.meta.client &&
    (to.query.page_no !== from.query.page_no || !isEqual(to.query.filter_ids, from.query.filter_ids))
  ) {
    fetchAthletics(to.query);
  }
  next();
});

async function fetchAthletics(query) {
  isLoading.value = true;
  totalPages.value = 0;
  const {
    games: nextGames,
    filters: nextFilters,
    totalPages: nextTotalPages,
    currentPage: nextCurrentPage,
  } = await setupAndFetchAthletics(mainStore, query, $axios);
  games.value = nextGames;
  filters.value = nextFilters;
  totalPages.value = nextTotalPages;
  currentPage.value = nextCurrentPage;
  isLoading.value = false;
}

function handleFilterChange(filterArr) {
  if (filterArr.length > 0) {
    return navigateTo({ query: { filter_ids: filterArr.join(',') } });
  }
  return navigateTo({ query: null });
}

function getDirections(loc) {
  const encodedLocation = encodeURIComponent(loc);
  const baseUrl = 'https://www.google.com/maps/dir/?api=1';
  return `${baseUrl}&destination=${encodedLocation}`;
}

function handleShowScores(game, idx) {
  if (!game.multi_line) {
    return;
  }
  if (openScores.value.includes(idx)) {
    openScores.value = openScores.value.filter((value) => value !== idx);
  } else {
    openScores.value.push(idx);
  }
}

function getTagName(id) {
  const filter = filters.value.find((curr) => curr.id === id);
  return (filter && filter.name) || '';
}
</script>

<style scoped lang="scss">
#athletics {
  .page-container-item {
    display: flex;
    justify-content: space-between;
  }
  .sport-tag {
    padding: 4px 12px;
    background: #f3f3f3;
    border-radius: 12px;
    font-size: 14px;
    margin-bottom: 8px;
    display: block;
    width: fit-content;
    font-weight: 450;
    letter-spacing: 0.4px;
  }
  .col-left {
    .datetime {
      color: var(--primary-color);
      margin-top: 38px;
      @media screen and (min-width: 1024px) {
        margin-top: 0;
      }
    }
    flex: 1;
    display: flex;
    align-items: start;
    justify-content: start;
    text-align: left;
    font-weight: 600;
    font-size: 17px;
  }
  .col-right {
    flex: 3;
    .tournament {
      background: url('../../assets/arrow-down.png') no-repeat 100% 50%;
      &.open {
        background: url('../../assets/arrow-up.png') no-repeat 100% 50%;
      }
    }
    .tournament-scores {
      color: #717171;
      margin-top: 10px;
    }
    .scores-box {
      color: var(--primary-color);
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 24px;
      line-height: 24px;
      span {
        font-size: 20px;
        color: #717171;
      }
      .score {
        color: #333;
      }
      .home-team,
      .home-score {
        margin-top: 10px;
      }
    }
    .location {
      margin-top: 18px;
      font-size: 18px;
      color: #444;
      a {
        color: inherit;
        text-transform: uppercase;
        font-size: 12px;
        margin-left: 8px;
      }
    }
  }
}
</style>
